import { useEffect, useRef, useState } from "react";

import { useMyTenants } from "@/lib/useMyTenants";
import { useChatStore } from "@/runtime/chatStore";

function tenantInitial(tenantId: string): string {
  const cleaned = tenantId.replace(/[^a-zA-Z0-9]/g, "");
  if (cleaned.length === 0) return "?";
  return cleaned[0].toUpperCase();
}

function TenantAvatar({ initial, size = "sm" }: { initial: string; size?: "sm" | "md" }) {
  return (
    <span
      aria-hidden="true"
      className={`${
        size === "md" ? "h-5 w-5" : "h-4 w-4"
      } type-caption inline-flex flex-shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--accent-primary)] font-bold leading-none text-[var(--text-on-accent)]`}
    >
      {initial}
    </span>
  );
}

export function TenantSwitcher() {
  const [open, setOpen] = useState(false);
  const activeTenantId = useChatStore((s) => s.activeTenantId);
  const setActiveTenant = useChatStore((s) => s.setActiveTenant);
  const tenants = useMyTenants();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (tenants.length < 2) {
    if (!activeTenantId) return null;
    return (
      <div
        className="type-caption flex items-center gap-1.5 rounded-[var(--radius-pill)] bg-[var(--surface-sunken)] px-2.5 py-1 text-[var(--text-secondary)] shadow-[var(--elev-ring)]"
        title={activeTenantId}
      >
        <TenantAvatar initial={tenantInitial(activeTenantId)} />
        <span className="max-w-[10ch] truncate">{activeTenantId}</span>
      </div>
    );
  }

  const displayedTenant = activeTenantId ?? tenants[0];

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="type-caption flex items-center gap-1.5 rounded-[var(--radius-pill)] bg-[var(--surface-sunken)] px-2.5 py-1 text-[var(--text-primary)] shadow-[var(--elev-ring)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
      >
        <TenantAvatar initial={tenantInitial(displayedTenant)} />
        <span className="max-w-[10ch] truncate">{displayedTenant}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`text-[var(--text-tertiary)] transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)] ${open ? "rotate-180" : ""}`}
        >
          <path d="M2.5 4l2.5 2.5L7.5 4" />
        </svg>
      </button>
      {open && (
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Switch tenant"
          className="bk-anim-message absolute bottom-[calc(100%+var(--space-1))] left-0 z-30 min-w-[12rem] max-w-[16rem] overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-raised)] shadow-[var(--elev-whisper-lg)] ring-1 ring-[var(--border-default)]"
        >
          <div className="type-overline border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-3 py-1.5 text-[var(--text-tertiary)]">
            Switch tenant
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {tenants.map((t) => {
              const active = t === activeTenantId;
              return (
                <li key={t}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      setActiveTenant(t);
                      setOpen(false);
                    }}
                    className={`type-body-sm flex w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] ${
                      active
                        ? "bg-[var(--accent-soft)] font-medium text-[var(--text-primary)]"
                        : "text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
                    }`}
                  >
                    <TenantAvatar initial={tenantInitial(t)} size="md" />
                    <span className="min-w-0 flex-1 truncate font-mono">{t}</span>
                    {active && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className="flex-shrink-0 text-[var(--accent-primary)]"
                      >
                        <path d="M2.5 6.5l2.5 2.5 4.5-5" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
